import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Users, DollarSign, Video, ShoppingBag, BookOpen, Check, X, Upload, TrendingUp, Crown, Lock, Unlock, Settings, RefreshCw, Globe, Edit3, FileSpreadsheet, AlertCircle, Download, Youtube, Plus, Layers, Calendar, Flag, Star, Trash2, Gift, Ban, UserX, UserCheck, CheckCircle, ShieldCheck, Clock, PartyPopper, Archive } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Videos tab.
 *
 * Lifted out of AdminPanel.js during the Phase 2 frontend split (Wave 4).
 * Pure presentational — receives parent state + handlers via props.
 */
export default function VideosTab(props) {
  const { user, navigate, getAuthHeader, fetchAllData, fileInputRef, users, setUsers, userSearch, setUserSearch, filteredUsers, pendingFounders, founderSettings, setFounderSettings, founderSeatsRemaining, updateFounderSettings, toggleFounderEnrollment, toggleFoundingMember, suspendUser, archiveUser, permanentlyDeleteUser, changeUserTier, sales, pendingSales, pendingListingSales, handleVerifySale, handleVerifyListingSale, handleUnverifyListingSale, handleCancelListingSale, commissionDashboard, promoCodes, newPromo, setNewPromo, createPromoCode, deletePromoCode, promoToDelete, setPromoToDelete, potData, setPotData, updatePot, advertisers, pendingVideos, pendingLibraryVideos, handleBatchApproveVideos, handleVideoReview, handleLibraryVideoReview, moveDownlineData, setMoveDownlineData, featuredMembers, selectedUserToFeature, setSelectedUserToFeature, featuredText, setFeaturedText, handleAddFeaturedMember, handleRemoveFeaturedMember, tierFeatures, editingTier, setEditingTier, editFeatures, setEditFeatures, startEditingTier, saveTierFeatures, customTiers, showTierForm, setShowTierForm, newTier, setNewTier, tierFeaturesInput, setTierFeaturesInput, handleCreateTier, handleDeleteTier, pendingServices, flaggedServices, handleApproveService, handleFlagService, handleDeleteService, edirectoryStatus, syncingEdir, syncEdirectory, importedTransactions, importing, importResult, handleFileSelect, downloadSampleCSV } = props;

  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const runReconcile = async () => {
    if (!window.confirm('Repoint every profile photo, course thumbnail and intro video record to the file that actually exists in storage? Member data is never removed — unrecoverable records are left untouched and counted.')) return;
    setReconciling(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/media/reconcile`, {}, { headers: getAuthHeader() });
      setReconcileResult(res.data);
      const total = ['profile_photos', 'course_thumbnails', 'intro_videos']
        .reduce((n, k) => n + (res.data[k]?.repointed || 0), 0);
      toast.success(total === 0 ? 'All media references are already healthy' : `Repointed ${total} broken reference(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reconciliation failed');
    } finally {
      setReconciling(false);
    }
  };
  const runRestore = async () => {
    if (!window.confirm('Re-link EMPTY photo/thumbnail/intro-video fields to files that still exist in storage? Existing values are never touched.')) return;
    setRestoring(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/media/restore-from-storage`, {}, { headers: getAuthHeader() });
      const total = ['profile_photos', 'scheduler_photos', 'course_thumbnails', 'intro_videos']
        .reduce((n, k) => n + (res.data[k] || 0), 0);
      toast.success(total === 0 ? 'Nothing to restore — no blank fields with recoverable files' : `Restored ${total} media reference(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="videos-tab">
      <h2 className="text-2xl font-playfair font-bold text-foreground mb-4">Video Management</h2>
            
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="wm-card p-6 rounded-lg">
                      <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                        <Upload className="w-5 h-5 text-primary" />
                        Featured Masterclass &amp; Milestone Videos
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Update the &quot;Featured Masterclass&quot; video on the landing page (MP4 upload or YouTube/Vimeo link) or the $1M celebration video.
                      </p>
                      <Link to="/admin/upload-milestone">
                        <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="upload-milestone-btn">
                          Upload / Update Videos
                        </Button>
                      </Link>
                    </div>

                    <div className="wm-card p-6 rounded-lg">
                      <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                        <Video className="w-5 h-5 text-primary" />
                        Manage Videos
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        View and manage all uploaded videos from founding members.
                      </p>
                      <Link to="/video-library">
                        <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary/10" data-testid="manage-videos-btn">
                          Manage All Videos
                        </Button>
                      </Link>
                    </div>

                    <div className="wm-card p-6 rounded-lg">
                      <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                        <RefreshCw className="w-5 h-5 text-primary" />
                        Media Health
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Repair broken images/videos: repoints every profile photo, course thumbnail and intro-video record to the file that actually exists in persistent storage. Never removes member data. Safe to run anytime.
                      </p>
                      <Button
                        variant="outline"
                        onClick={runReconcile}
                        disabled={reconciling}
                        className="w-full border-primary text-primary hover:bg-primary/10"
                        data-testid="reconcile-media-btn"
                      >
                        {reconciling ? 'Reconciling…' : 'Reconcile Media References'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={runRestore}
                        disabled={restoring}
                        className="w-full border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 mt-2"
                        data-testid="restore-media-btn"
                      >
                        {restoring ? 'Restoring…' : 'Restore Cleared Media'}
                      </Button>
                      {reconcileResult && (
                        <div className="text-xs text-muted-foreground mt-3 space-y-1" data-testid="reconcile-result">
                          {['profile_photos', 'course_thumbnails', 'intro_videos'].map(k => (
                            <p key={k}>
                              {k.replace(/_/g, ' ')}: {reconcileResult[k]?.checked || 0} checked, {reconcileResult[k]?.ok || 0} ok,{' '}
                              <span className="text-emerald-400">{reconcileResult[k]?.repointed || 0} repointed</span>,{' '}
                              <span className="text-amber-400">{reconcileResult[k]?.unrecoverable || 0} unrecoverable (left untouched)</span>
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="wm-card p-6 rounded-lg">
                    <h3 className="font-semibold text-foreground mb-4">Video Upload Instructions</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• Only founding members and admins can upload videos</li>
                      <li>• Maximum file size: 4GB</li>
                      <li>• Supported format: MP4 only</li>
                      <li>• Videos are hosted on the platform server</li>
                      <li>• Videos can be assigned to specific membership tiers</li>
                    </ul>
                  </div>
    </div>
  );
}
